import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ProductCard = ({ product }) => {
  return (
    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}>
      <div className="group product-card bg-white border border-neutral-100 rounded-2xl overflow-hidden h-full flex flex-col transition-all duration-500 hover:shadow-2xl hover:shadow-[#FF4D00]/10 hover:-translate-y-2 hover:border-[#FF4D00]/30">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-neutral-50 to-neutral-100">
          {/* Decorative Corner */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-[#FF4D00]/10 to-transparent z-10"></div>
          
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
          />
          
          {/* Overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          
          {/* B2B Badge */}
          <div className="absolute top-3 left-3 z-20">
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              B2B
            </div>
          </div>

          {/* Premium Badge */}
          {product.is_premium && (
            <div className="absolute top-3 right-3 z-20">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                Premium
              </div>
            </div>
          )}
          
          {/* Quick View on Hover */}
          <div className="absolute bottom-4 left-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-4 group-hover:translate-y-0">
            <span className="block w-full text-center bg-white/95 backdrop-blur-sm text-neutral-900 py-2 rounded-lg text-sm font-semibold">
              View Details
            </span>
          </div>

          {!product.in_stock && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-30">
              <span className="text-white font-bold text-lg bg-red-500 px-4 py-2 rounded-lg">Contact for Availability</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 flex-1 flex flex-col">
          <div className="flex-1">
            {/* Category Tag */}
            <span className="text-xs font-medium text-[#FF4D00] uppercase tracking-wider">
              {product.category?.replace(/-/g, ' ')}
            </span>
            
            <h3 className="font-bold text-base mt-1 mb-2 line-clamp-2 group-hover:text-[#FF4D00] transition-colors duration-300">
              {product.name}
            </h3>
            
            {/* Rating */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full">
                <Star className="w-3.5 h-3.5 fill-green-600 text-green-600" />
                <span className="text-sm font-bold text-green-700">{product.rating}</span>
              </div>
              <span className="text-xs text-neutral-500">({product.reviews_count} reviews)</span>
            </div>

            {/* Features */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-1.5">
                {product.features?.slice(0, 2).map((feature, index) => (
                  <span
                    key={index}
                    className="text-xs bg-neutral-100 text-neutral-600 px-2.5 py-1 rounded-full border border-neutral-200"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <Button 
            className="w-full bg-gradient-to-r from-[#FF4D00] to-orange-500 hover:from-[#e64500] hover:to-orange-600 text-white rounded-xl font-bold shadow-lg shadow-[#FF4D00]/20 group-hover:shadow-xl group-hover:shadow-[#FF4D00]/30 transition-all duration-300"
            data-testid={`request-quote-${product.id}`}
          >
            Request Quote
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
